import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AddButton,
  CancelButton,
  SubmitButton,
  UpdateButton,
} from "../../../components/mui/Buttons/Buttons";
import { FieldArray, Form, FormikProvider, useFormik } from "formik";
import {
  Autocomplete,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import GlobalDialog from "../../../components/mui/Dialogue/GlobalDialog";
import GlobalSelectField from "../../../components/mui/GlobalSelectField";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import {
  addCategory,
  getAllCategory,
  getCategoryTypesNoLimit,
} from "../../../redux/features/assetManagementSlice";
import Loader3D from "../../../components/Loader/Loader3D";
import AddType from "../Type/AddType";
import { Icon } from "@iconify/react/dist/iconify.js";

const Addcategory = ({ dispatchParams }) => {
  const [open, setOpen] = useState(false);

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { loading, categoryTypesNoLimit } = useSelector(
    (state) => state.assetmanagement
  );

  const formikRef = useRef();

  const validationSchema = useMemo(() => {
    const isAssetType =
      categoryTypesNoLimit?.find(
        (ct) =>
          ct.category_type_id === formikRef?.current?.values?.category_type
      )?.is_returnable === true;

    return Yup.object().shape({
      category_name: Yup.string().required("Please add category name"),
      category_type: Yup.string().required("Choose Type"),
      sub_category: Yup.array().of(
        Yup.object().shape({
          sub_category_prefix: isAssetType
            ? Yup.string().nullable().required("Required for returnalble type")
            : Yup.string().nullable(),
        })
      ),
    });
  }, [categoryTypesNoLimit, formikRef?.current?.values?.category_type]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      category_name: "",
      category_description: "",
      category_type: "",
      sub_category: [
        {
          sub_category_name: "",
          sub_category_prefix: null,
          min_stock_quantity: null,
          min_stock_quantity: null,
        },
      ],
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      dispatch(addCategory({ data: [values] })).then((res) => {
        if (res.payload.status === "success") {
          enqueueSnackbar(res.payload.message, { variant: "success" });
          dispatch(getAllCategory(dispatchParams));
          setOpen(false);
          formik.resetForm();
        } else {
          enqueueSnackbar(res.payload.message, { variant: "error" });
        }
      });
    },
  });
  formikRef.current = formik;

  useEffect(() => {
    if (open) {
      dispatch(getCategoryTypesNoLimit());
    }
  }, [open]);

  return (
    <GlobalDialog
      title={"Add Category"}
      open={open}
      setOpen={setOpen}
      fullWidth
      maxWidth={"md"}
      closeButton={true}
      popupButton={
        <AddButton size="medium" action={() => setOpen(true)}>
          Add Category
        </AddButton>
      }
      content={
        <>
          <FormikProvider value={formik}>
            <Form onSubmit={formik.handleSubmit}>
              {loading && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    bgcolor: "rgba(255,255,255,0.6)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 10,
                  }}
                >
                  <Loader3D />
                </Box>
              )}
              <Box>
                <Stack direction={"column"} spacing={2}>
                  <TextField
                    name="category_name"
                    value={formik.values.category_name}
                    error={
                      formik.touched.category_name &&
                      Boolean(formik.errors.category_name)
                    }
                    helperText={formik.errors.category_name}
                    onChange={formik.handleChange}
                    fullWidth
                    label="Category"
                  />
                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}
                  >
                    <AddType />
                  </Box>
                  <GlobalSelectField
                    options={{
                      formik,
                      name: "category_type",
                      label: "Type",
                      data: categoryTypesNoLimit,
                      keys: {
                        id: "category_type_id",
                        value: "category_type_name",
                      },
                    }}
                  />

                  <FieldArray name="sub_category">
                    {({ push, remove }) => (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                          border: "1px solid #007090",
                          borderRadius: "8px",
                          padding: "16px",
                          marginBottom: 2,
                        }}
                      >
                        <Typography variant="subtitle2" color="primary">
                          Add Sub Categories
                        </Typography>

                        {formik.values.sub_category.map((_, index) => (
                          <Stack
                            key={index}
                            direction={{
                              xs: "column",
                              sm: "row",
                            }}
                            spacing={1}
                          >
                            <TextField
                              name={`sub_category[${index}].sub_category_name`}
                              value={
                                formik.values.sub_category[index]
                                  .sub_category_name
                              }
                              onChange={formik.handleChange}
                              label="Sub Category"
                              fullWidth
                            />
                            <TextField
                              name={`sub_category[${index}].sub_category_prefix`}
                              value={
                                formik.values.sub_category[index]
                                  .sub_category_prefix
                              }
                              onChange={(e) => {
                                const uppercaseValue =
                                  e.target.value.toUpperCase();
                                formik.setFieldValue(
                                  `sub_category[${index}].sub_category_prefix`,
                                  uppercaseValue
                                );
                              }}
                              error={
                                !!formik.errors.sub_category?.[index]
                                  ?.sub_category_prefix &&
                                !!formik.touched.sub_category?.[index]
                                  ?.sub_category_prefix
                              }
                              helperText={
                                formik.touched.sub_category?.[index]
                                  ?.sub_category_prefix &&
                                formik.errors.sub_category?.[index]
                                  ?.sub_category_prefix
                              }
                              label="Prefix"
                              fullWidth
                            />
                            <TextField
                              name={`sub_category[${index}].min_stock_quantity`}
                              value={
                                formik.values.sub_category[index]
                                  .min_stock_quantity
                              }
                              onChange={formik.handleChange}
                              label="Minimum Stock Quantity"
                              fullWidth
                              type="number"
                            />

                            <Button
                              onClick={() => remove(index)}
                              variant="outlined"
                              color="error"
                            >
                              <Icon
                                icon={"material-symbols:delete"}
                                width={20}
                                height={20}
                              />
                            </Button>
                          </Stack>
                        ))}

                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() =>
                            push({
                              sub_category_name: "",
                              sub_category_prefix: null,
                              min_stock_quantity: "",
                            })
                          }
                          startIcon={<Icon icon="mdi:plus" />}
                        >
                          Add More
                        </Button>
                      </Box>
                    )}
                  </FieldArray>

                  <TextField
                    name="category_description"
                    value={formik.values.category_description}
                    onChange={formik.handleChange}
                    fullWidth
                    label="Description"
                  />
                </Stack>
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", pt: 2 }}
                >
                  <Stack direction={"row"} spacing={1}>
                    <CancelButton size="small" action={() => setOpen(false)}>
                      Cancel
                    </CancelButton>
                    <SubmitButton disabled={loading} size="small" type="submit">
                      {loading ? "Please wait..." : "Save"}
                    </SubmitButton>
                  </Stack>
                </Box>
              </Box>
            </Form>
          </FormikProvider>
        </>
      }
    />
  );
};

export default Addcategory;
